/* The savings ledger card — what acting on Zana's findings has been worth.
 *
 * This is the renewal argument. An owner deciding whether to keep paying does
 * not re-read the feature list; they ask what it got them. Before this, the
 * product forgot every win the week after it happened.
 *
 * The wording is careful on purpose. These are problems that STOPPED costing
 * money — a real litres×price run-rate, not a receipt — so the card says
 * "worth" and never "saved to date", and it always offers the breakdown. A
 * savings counter nobody can audit is the first thing a sceptical buyer pokes,
 * and it should survive being poked.
 *
 * Renders nothing at all until there is something real to show. An empty
 * "Ksh 0 saved" tile on a new fleet is worse than no tile.
 */

import React, { useCallback, useEffect, useState } from "react";
import { auth } from "../firebase";

const BASE = (process.env.REACT_APP_PAYMENTS_URL || "").replace(/\/$/, "");

interface Basis {
  title: string;
  busId: string | null;
  savingsKES: number;
  fixedAt: number;
}

interface Savings {
  lifetimeKES: number;
  thisMonthKES: number;
  lastMonthKES: number;
  entryCount: number;
  firstMonth: string | null;
  monthsTracked: number;
  basis: Basis[];
}

const ksh = (n: number) => `Ksh ${Math.round(n).toLocaleString("en-US")}`;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** "2026-03" → "March 2026", for the "since …" line. */
function monthLabel(key: string | null): string | null {
  if (!key) return null;
  const [y, m] = key.split("-");
  const idx = Number(m) - 1;
  if (!MONTHS[idx]) return null;
  return `${MONTHS[idx]} ${y}`;
}

export default function SavingsLedgerCard({ fleetOrgCode }: { fleetOrgCode: string }) {
  const [savings, setSavings] = useState<Savings | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!fleetOrgCode || !BASE) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return;
      const res = await fetch(
        `${BASE}/api/premium/zana/savings?orgCode=${encodeURIComponent(fleetOrgCode)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.ok && data.savings) setSavings(data.savings);
    } catch {
      /* The card is additive — a failure here must never break the dashboard. */
    }
  }, [fleetOrgCode]);

  useEffect(() => {
    void load();
  }, [load]);

  // Nothing banked yet: say nothing rather than showing a zero.
  if (!savings || savings.lifetimeKES <= 0) return null;

  const since = monthLabel(savings.firstMonth);

  return (
    <div
      data-zt="savingsCard"
      style={{
        background: "linear-gradient(135deg,#f5f3ff,#eef2ff)",
        border: "1px solid #ddd6fe",
        borderRadius: 14,
        padding: "16px 18px",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: "#4c1d95", lineHeight: 1.1 }}>
          {ksh(savings.lifetimeKES)}
        </div>
        <div style={{ fontSize: 13, color: "#5b21b6", fontWeight: 600 }}>
          worth of problems fixed{since ? ` since ${since}` : ""}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            marginLeft: "auto",
            border: "1px solid #ddd6fe",
            background: "#fff",
            color: "#5b21b6",
            borderRadius: 8,
            padding: "5px 11px",
            fontSize: 12.5,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {open ? "Hide breakdown" : "What is this?"}
        </button>
      </div>

      <div style={{ marginTop: 6, fontSize: 12.5, color: "#6d28d9" }}>
        {savings.thisMonthKES > 0
          ? `${ksh(savings.thisMonthKES)} this month`
          : savings.lastMonthKES > 0
          ? `${ksh(savings.lastMonthKES)} last month`
          : `${savings.entryCount} ${savings.entryCount === 1 ? "finding" : "findings"} resolved`}
      </div>

      {open && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #ddd6fe" }}>
          <p style={{ fontSize: 12.5, color: "#5b21b6", margin: "0 0 10px", lineHeight: 1.6 }}>
            Each line is a priced problem Zana raised that has since stopped firing. The figures are
            litres × the pump price on file — money that stopped being at risk, not money banked.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {savings.basis.map((b, i) => (
              <div
                key={`${b.title}-${b.fixedAt}-${i}`}
                style={{
                  display: "flex",
                  gap: 10,
                  fontSize: 12.5,
                  color: "#4c1d95",
                  background: "#fff",
                  borderRadius: 8,
                  padding: "7px 10px",
                }}
              >
                <span style={{ flex: 1 }}>{b.title}</span>
                <strong style={{ whiteSpace: "nowrap" }}>{ksh(b.savingsKES)}</strong>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
